import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useRouteMatch, matchPath } from 'react-router-dom';
import { EventEmitter } from 'eventemitter3';
import { AlertCard, COLORS, Pill } from '@livecommerce/design-system';
import { styled } from '@linaria/react';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { CircleFlair, TopRightFlair } from 'components-v3/Flairs/FlairPrimitives.jsx';
import MetaRefreshBanner from 'pages/AppIntegrations/MetaRefreshBanner.jsx';
import { textToHtml } from 'helpers/textToHtml';
import { PROMOTED_SHOWS_KNOWLEDGE_BASE_URL } from 'pages/LandingPage/constants.js';

const CACHE_KEY = 'dismissed_callouts';
const globalStateEmitter = new EventEmitter();

const StyledAlertCard = styled(AlertCard)`
  background-color: ${COLORS.yellow_300};
  border: none;
  border-radius: 16px;
`;

const StyledSecondaryAlertCard = styled(AlertCard)`
  background-color: ${COLORS.blue_200};
  border: none;
  border-radius: 16px;
`;

const StyledMetaRefreshBanner = styled(MetaRefreshBanner)`
  border: none;
  border-radius: 16px;

  div {
    border-radius: 16px;
  }
`;

const onDismissGroup = (group) => {
  if (window.localStorage) {
    const dismissed = (window.localStorage.getItem(CACHE_KEY) || '').split(',');
    if (!dismissed.includes(group)) {
      window.localStorage.setItem(CACHE_KEY, dismissed.filter(Boolean).concat(group).join(','));
    }
  }
  globalStateEmitter.emit('bump');
};

const AlertWithDismiss = ({ group, title, children }) => {
  const onClose = useCallback(() => {
    onDismissGroup(group);
  }, [group]);

  return (
    <StyledAlertCard variant="info" title={title} hasBorder={true} isCloseButtonShown={true} onClose={onClose}>
      {children}
    </StyledAlertCard>
  );
};

const SecondaryAlert = ({ title, children }) => {
  return (
    <StyledSecondaryAlertCard variant="info" title={title} hasBorder={true} isCloseButtonShown={false}>
      {children}
    </StyledSecondaryAlertCard>
  );
};

const useCallouts = ({ id, type } = {}) => {
  const [_, setVersion] = useState(0); // Just used to force refresh
  const dismissed = (window.localStorage.getItem(CACHE_KEY) || '').split(',');
  const now = new Date();
  const location = useLocation();
  const routeMatch = useRouteMatch();
  const { features } = useFeatureContext();
  const { t } = useLocalization();
  const searchParams = new URLSearchParams(location.search);

  const showRedirectionCallout = !!searchParams.get('redirectTo')

  // This is probably hacky but it'll do for now: Use a global event emitter to
  // keep the last seen item in sync between multiple instances of the component
  useEffect(() => {
    const onUpdate = () => setVersion((prevVersion) => prevVersion + 1);
    globalStateEmitter.on('bump', onUpdate);
    return () => globalStateEmitter.off('bump', onUpdate);
  }, []);

  if (!features.dashboardCalloutsEnabled) {
    return {
      callouts: [],
    };
  }

  // Define CALLOUTS inside the hook so it can access the t function
  const CALLOUTS = [
    // {
    //   group: 'new-feature-a',
    //   type: 'gutterItem',
    //   validTime: [ new Date('2021-09-01'), new Date('2025-09-30') ],
    //   content: <AlertWithDismiss group="new-feature-a" title="Other group!!" />,
    //   pages: [ /on-demand\/pages/ ],
    // },
    // Feature VOD-product-actions
    {
      group: 'vod-product-actions',
      id: 'sidebarFlair-playlist',
      validTime: [new Date('2021-09-01'), new Date('2025-09-30')],
      content: <CircleFlair outerColor={COLORS.dark_700} />,
      pages: [/on-demand/],
      features: ['externalProductCardVod'],
    },
    {
      group: 'vod-product-actions',
      type: 'gutterItem',
      validTime: [new Date('2021-09-01'), new Date('2025-09-30')],
      content: (
        <AlertWithDismiss group="vod-product-actions" title={t('callouts.vod.product.actions.title')}>
          {t('callouts.vod.product.actions.description')}
        </AlertWithDismiss>
      ),
      pages: [/on-demand\/pages/],
      features: ['externalProductCardVod'],
    },
    {
      group: 'vod-product-actions',
      ids: [
        'containerDetails-tabMenu-settings',
        'accordionSectionFlair-containerPlayerSettings',
        'containerPlayerSettings-productAction',
        'productActionSelectItem-modal',
        'productActionSelectItem-drawer',
        'productActionSelectItem-none',
      ],
      validTime: [new Date('2021-09-01'), new Date('2025-09-30')],
      content: <TopRightFlair title="New" />,
      pages: [/on-demand\/pages\/[a-zA-Z0-9_-]+\/playlists\/[a-zA-Z0-9_-]+/],
      features: ['externalProductCardVod'],
    },

    // Meta refresh banner (notice for social media integrations)
    {
      id: 'meta-refresh-banner',
      pages: [/on-demand\/integrations/, /live\/integrations/],
      type: 'gutterItem',
      content: <StyledMetaRefreshBanner />,
    },

    // Embeddable Insights
    {
      group: 'embeddable-insights',
      ids: ['sidebarFlair-insights'],
      content: <CircleFlair outerColor={COLORS.dark_700} />,
      pages: [/live/],
      features: ['showStatsEmbeddableInsights'],
    },
    {
      group: 'embeddable-insights',
      ids: ['sidebarFlair-sub-menu-insights'],
      content: <TopRightFlair title="New" tooltipText={t('callouts.embeddable.submenu.tooltip')} minWidth="180px" />,
      pages: [/live/],
      features: ['showStatsEmbeddableInsights'],
    },
    {
      group: 'embeddable-showsList',
      ids: ['sidebarFlair-sub-menu-showsList'],
      content: (
        <TopRightFlair title="New" tooltipText={t('callouts.statsShowsList.submenu.tooltip')} minWidth="180px" />
      ),
      pages: [/live/],
      features: ['showStatsShowsList'],
    },
    {
      group: 'embeddable-insights',
      type: 'gutterItem',
      content: (
        <AlertWithDismiss group="embeddable-insights" title={t('callouts.embeddable.alert.title')}>
          {t('callouts.embeddable.alert.description')}
        </AlertWithDismiss>
      ),
      pages: [/live\/embed-stats/],
      features: ['showStatsEmbeddableInsights'],
    },
    {
      group: 'embeddable-showsList',
      type: 'gutterItem',
      content: (
        <AlertWithDismiss group="embeddable-showsList" title={t('callouts.showsList.alert.title')}>
          {t('callouts.showsList.alert.description')}
        </AlertWithDismiss>
      ),
      pages: [/live\/stats\/showsList/],
      features: ['showStatsShowsList'],
    },

    // Embeddable insights VOD
    {
      group: 'embeddable-insights',
      ids: ['sidebarFlair-insights'],
      content: <CircleFlair outerColor={COLORS.dark_700} />,
      pages: [/on-demand/],
      features: ['vodStatsEmbeddableInsights'],
    },
    {
      group: 'embeddable-insights',
      ids: ['sidebarFlair-sub-menu-insights'],
      content: <TopRightFlair title="New" tooltipText={t('callouts.embeddable.submenu.tooltip')} minWidth="180px" />,
      pages: [/on-demand/],
      features: ['vodStatsEmbeddableInsights'],
    },
    {
      group: 'embeddable-insights',
      type: 'gutterItem',
      content: (
        <AlertWithDismiss group="embeddable-insights" title={t('callouts.embeddable.alert.title')}>
          {t('callouts.embeddable.vod.alert.description')}
        </AlertWithDismiss>
      ),
      pages: [/on-demand\/embed-stats/],
      features: ['vodStatsEmbeddableInsights'],
    },
    // VOD videos bulk actions and pages search
    {
      group: 'vod-pages-search',
      validTime: [new Date('2025-07-01'), new Date('2026-01-01')],
      id: 'sidebarFlair-playlist',
      content: <CircleFlair outerColor={COLORS.dark_700} />,
      pages: [/on-demand/],
      features: ['vodVideosBulkActions'],
    },
    {
      group: 'vod-videos-bulk-actions',
      validTime: [new Date('2025-07-01'), new Date('2026-01-01')],
      id: 'sidebarFlair-videos',
      content: <CircleFlair outerColor={COLORS.dark_700} />,
      pages: [/on-demand/],
      features: ['vodVideosBulkActions'],
    },
    {
      group: 'vod-pages-search',
      type: 'gutterItem',
      validTime: [new Date('2025-08-26'), new Date('2026-09-01')],
      content: (
        <AlertWithDismiss group="vod-pages-search" title={t('callouts.vod.pages.improved.search.title')}>
          {t('callouts.vod.pages.improved.search.description')}
        </AlertWithDismiss>
      ),
      pages: [/on-demand\/pages/],
      features: ['vodVideosBulkActions'],
    },
    {
      group: 'vod-videos-bulk-actions',
      type: 'gutterItem',
      validTime: [new Date('2025-08-26'), new Date('2026-09-01')],
      content: (
        <AlertWithDismiss group="vod-videos-bulk-actions" title={t('callouts.vod.videos.bulk.actions.title')}>
          {t('callouts.vod.videos.bulk.actions.description')}
        </AlertWithDismiss>
      ),
      pages: [/on-demand\/videos/],
      features: ['vodVideosBulkActions'],
    },
    {
      group: 'live-promotedShows-in-player',
      type: 'gutterItem',
      validTime: [new Date('2025-09-04'), new Date('2026-09-01')],
      content: (
        <AlertWithDismiss group="live-promotedShows-in-player" title={t('callouts.live.promotedShows-in-player.show-setup.title')}>
          {textToHtml(t('callouts.live.promotedShows-in-player.show-setup.description', { url: PROMOTED_SHOWS_KNOWLEDGE_BASE_URL }))}
        </AlertWithDismiss>
      ),
      pages: [/live\/shows\/[a-zA-Z0-9_-]+\/setup/, /live\/shows$/],
      features: ['promotedShowsInPlayer'],
    },
    {
      group: 'live-promotedShows-in-player',
      type: 'gutterItem',
      validTime: [new Date('2025-09-04'), new Date('2026-09-01')],
      content: (
        <AlertWithDismiss group="live-promotedShows-in-player" title={t('callouts.live.promotedShows-in-player.channels-setup.title')}>
          {textToHtml(t('callouts.live.promotedShows-in-player.channels-setup.description', { url: PROMOTED_SHOWS_KNOWLEDGE_BASE_URL }))}
        </AlertWithDismiss>
      ),
      pages: [/live\/widgets\/channels/, /live\/widgets/],
      features: ['promotedShowsInPlayer'],
    },
    {
      group: 'vod-distribution-v2',
      type: 'gutterItem',
      content: (
        <SecondaryAlert>
          {t('callouts.vod.playlist-redirection.description')}
        </SecondaryAlert>
      ),
      pages: [/on-demand\/playlists\/new/, /on-demand\/playlists\/[a-zA-Z0-9_-]+/],
      condition: showRedirectionCallout,
    },
    {
      group: 'vod-distribution-v2',
      type: 'gutterItem',
      content: (
        <SecondaryAlert>
          {t('callouts.vod.appearance-redirection.description')}
        </SecondaryAlert>
      ),
      pages: [/on-demand\/appearances/],
      condition: showRedirectionCallout,
    },
    {
      group: 'live-promotedShows-in-player',
      ids: [
        'landingPageSettings-enablePromotedShows-checkbox',
      ],
      validTime: [new Date('2025-09-04'), new Date('2026-09-01')],
      content: <Pill variant="warning">{t('genericLabels.new')}</Pill>,
      pages: [/live\/widgets\/channels/],
      features: ['promotedShowsInPlayer'],
    },
  ];

  const callouts = CALLOUTS
    // Skip dismissed callouts
    .filter((callout) => !dismissed.includes(callout.group))
    // Skip callouts that are not valid today
    .filter((callout) => {
      if (!callout.validTime) return true;
      const [startDate, endDate] = callout.validTime;
      return now >= startDate && now <= endDate;
    })
    // Skip callouts that are not relevant to the current page
    .filter((callout) => {
      if (!callout.pages?.length) return true;
      return callout.pages.some((pagePattern) => {
        if (pagePattern instanceof RegExp) return pagePattern.test(location.pathname);
        if (typeof pagePattern !== 'function') return false;

        try {
          return pagePattern(location, routeMatch);
        } catch (err) {
          console.error('Error evaluating callout page:', err);
          return false;
        }
      });
    })
    .filter((callout) => {
      if (!type) return true;
      return callout.type === type;
    })
    .filter((callout) => {
      if (!id) return true;
      return callout.id === id || callout.ids?.includes(id);
    })
    .filter((callout) => {
      if (!callout.features || callout.features.length === 0) return true;
      return callout.features.every((feature) => features[`${feature}Enabled`]);
    })
    .filter((callout) => {
      if (!callout.condition) return true;
      return callout.condition;
    })
    .filter((callout) => {
      return callout.condition ?? true;
    });


  return {
    callouts,
  };
};

export default useCallouts;
